import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/" className="flex items-center cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/91f0f8e12b1f19026b27ce5c4f6b9182/797347f91d9f733f37410874a441f5ee.png" 
                alt="AI Consulting & Automation" 
                className="h-10 w-auto"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <a href="/" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap text-sm">
              Home
            </a>
            <a href="/ai-consulting" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap text-sm">
              AI Consulting
            </a>
            <a href="/ai-book" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap text-sm">
              AI Books
            </a>
            <a href="/real-estate" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap text-sm">
              Real Estate
            </a>

            {/* Resources Dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap text-sm">
                Resources
                <i className="ri-arrow-down-s-line text-gray-400 text-base group-hover:rotate-180 transition-transform duration-200"></i>
              </button>
              <div className="absolute left-0 top-full mt-1 w-60 bg-white border border-gray-100 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 py-1">
                <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">Tax Foreclosure Maps</div>
                <a href="/ca-tax-foreclosures" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                  <i className="ri-map-pin-2-line text-orange-400"></i>
                  CA Tax Foreclosures
                </a>
                <a href="/nv-tax-foreclosures" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                  <i className="ri-map-pin-2-line text-amber-400"></i>
                  NV Tax Foreclosures
                </a>
                <a
                  href="https://realestatedevelopmentauthority.net/az-tax-sales"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-map-pin-2-line text-green-500"></i>
                  AZ Tax Sales
                  <i className="ri-external-link-line text-gray-300 text-xs ml-auto"></i>
                </a>
                <a href="/tax-foreclosures" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                  <i className="ri-map-2-line text-amber-500"></i>
                  All States (CA+NV+AZ)
                </a>
                <hr className="border-gray-100 mx-3 my-1" />
                <a href="/foreclosure-recovery" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                  <i className="ri-route-line text-gray-400"></i>
                  Recovery Workflow
                </a>
                <hr className="border-gray-100 mx-3 my-1" />
                <a href="/store" className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                  <i className="ri-store-2-line text-gray-400"></i>
                  Store
                </a>
              </div>
            </div>

            <a href="/travel-authority" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap text-sm">
              Travel
            </a>
            <a href="/calendar" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap text-sm">
              Calendar
            </a>
            <a
              href="/store"
              className="flex items-center gap-1.5 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-store-2-line"></i>
              Store
            </a>
            {user ? (
              <div className="relative group">
                <button className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-6 h-6 flex items-center justify-center bg-gray-900 rounded-full text-white text-xs font-bold">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  {user.name.split(' ')[0]}
                  <i className="ri-arrow-down-s-line text-gray-400"></i>
                </button>
                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-100 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <a href="/profile" className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-xl cursor-pointer">
                    <i className="ri-user-line text-gray-400"></i> My Profile
                  </a>
                  <a href="/store" className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                    <i className="ri-shopping-bag-line text-gray-400"></i> My Purchases
                  </a>
                  <hr className="border-gray-100 mx-2" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 rounded-b-xl cursor-pointer"
                  >
                    <i className="ri-logout-box-r-line"></i> Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <a
                href="/login"
                className="flex items-center gap-1.5 border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-line"></i>
                Sign In
              </a>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-gray-200">
            <div className="flex flex-col space-y-3 pt-4">
              <a href="/" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Home</a>
              <a href="/ai-consulting" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">AI Consulting Authority</a>
              <a href="/ai-book" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">AI Book Authority</a>
              <a href="/real-estate" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Real Estate Authority</a>

              {/* Mobile Resources accordion */}
              <div>
                <button
                  onClick={() => setIsMobileResourcesOpen(v => !v)}
                  className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Resources
                  <i className={`ri-arrow-${isMobileResourcesOpen ? 'up' : 'down'}-s-line text-gray-400`}></i>
                </button>
                {isMobileResourcesOpen && (
                  <div className="ml-4 mt-2 flex flex-col space-y-2 border-l-2 border-gray-100 pl-3">
                    <a href="/ca-tax-foreclosures" className="text-gray-600 hover:text-gray-900 text-sm cursor-pointer">CA Tax Foreclosures</a>
                    <a href="/nv-tax-foreclosures" className="text-gray-600 hover:text-gray-900 text-sm cursor-pointer">NV Tax Foreclosures</a>
                    <a href="https://realestatedevelopmentauthority.net/az-tax-sales" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900 text-sm cursor-pointer flex items-center gap-1">
                      AZ Tax Sales <i className="ri-external-link-line text-xs text-gray-400"></i>
                    </a>
                    <a href="/tax-foreclosures" className="text-gray-600 hover:text-gray-900 text-sm cursor-pointer">All States Map</a>
                    <a href="/foreclosure-recovery" className="text-gray-600 hover:text-gray-900 text-sm cursor-pointer">Recovery Workflow</a>
                    <a href="/store" className="text-gray-600 hover:text-gray-900 text-sm cursor-pointer">Store</a>
                  </div>
                )}
              </div>

              <a href="/travel-authority" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Travel Authority</a>
              <a href="/books" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Books</a>
              <a href="/calendar" className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Calendar</a>
              <hr className="border-gray-100" />
              <a
                href="/store"
                className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5 rounded-lg font-semibold text-sm cursor-pointer whitespace-nowrap w-fit"
              >
                <i className="ri-store-2-line"></i> Store
              </a>
              {user ? (
                <div className="flex flex-col space-y-2">
                  <a href="/profile" className="flex items-center gap-2 text-gray-700 hover:text-gray-900 cursor-pointer">
                    <i className="ri-user-line"></i> My Profile ({user.name.split(' ')[0]})
                  </a>
                  <button onClick={handleLogout} className="flex items-center gap-2 text-red-500 cursor-pointer text-sm">
                    <i className="ri-logout-box-r-line"></i> Sign Out
                  </button>
                </div>
              ) : (
                <a href="/login" className="flex items-center gap-2 text-gray-700 hover:text-gray-900 cursor-pointer whitespace-nowrap">
                  <i className="ri-user-line"></i> Sign In
                </a>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
